import {
  EuiButtonIcon,
  EuiCode,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopover,
  EuiToolTip,
} from "@elastic/eui";
import { QuestionnaireHeaderProps } from "../../";
import {
  AnketaActionsEnum,
  AnketaApiToken,
  AnketaResourcesEnum,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useApi } from "src/api";
import {
  EvaluationContext,
  useAnketa,
} from "src/questionnaires/components/context_provider";
import { countIssues } from "src/questionnaires/components/issues";
import { useParams } from "react-router";
import { useToast } from "src/notifications";
import { useContext, useEffect, useState } from "react";
import { SelectOrInviteMemberModal } from "src/questionnaires/create/members/add";
import { OnboardProjectModal } from "./onboard_modal";
import { useSearchParams } from "react-router-dom";
import { useAnketaAuth } from "../../../auth/context";
import { getItemsByKeyPrefix } from "src/helper";
import {
  QuestionnaireSaveIndicator,
  useQuestionnaireSave,
} from "src/questionnaires/save";

export const QuestionnaireHeader = ({
  setModalIsVisible,
}: QuestionnaireHeaderProps): JSX.Element => {
  const { checkPermission, keycloak } = useAnketaAuth();

  const { translate } = useAnketaI18n();
  const { Put } = useApi();
  const anketaCtx = useAnketa();
  const { catalog } = anketaCtx;
  const evalCtx = useContext(EvaluationContext);
  const { addToast } = useToast();
  const { runSave } = useQuestionnaireSave();
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useParams();
  const { catalogId, questionnaireId } = params;

  const [modal, setModal] = useState<JSX.Element | null>(null);
  const [showApiToken, setShowApiToken] = useState<boolean>(false);
  // Render the onboarding modal inline (driven by this flag) instead of storing a
  // <OnboardProjectModal> element in `modal` state: an element stored in state freezes
  // its props at creation time, so it would keep a stale/empty vpcName even after qName
  // resolves. Inline rendering always reflects the current qName.
  const [showOnboard, setShowOnboard] = useState<boolean>(false);

  const [qName, setQName] = useState<string>("");

  // Onboarding creates/updates the infrastructure project (the bootstrap), so the
  // modal is an affordance for operators only. UI gate mirrors the server rule
  // (opsanio/bootstrap·create) - the route stays the boundary.
  const canBootstrap = checkPermission(
    AnketaResourcesEnum.opsanioBootstrap,
    AnketaActionsEnum.create,
  );

  // Onboarding needs a full owner-project-stage VPC name; without one the
  // button stays visible but disabled, so it is clear that the questionnaire -
  // not the permission - is what is missing.
  const [vpcOwner, vpcProject, vpcStage] = qName.split("-");
  const canOnboardQuestionnaire = !!(vpcOwner && vpcProject && vpcStage);

  useEffect(() => {
    const facts = catalog.factsToObject() as Record<string, any>;
    const name = getItemsByKeyPrefix(facts, "networks.vpc.vpc_name")[""];
    setQName(typeof name === "string" ? name : "");
  }, [catalog]);
  // Manual save (header button). Routes through the shared save engine so the
  // status indicator reflects it. Saving deliberately does NOT offer onboarding -
  // creating the infrastructure project is its own operator action behind its own
  // button (opsanio/bootstrap·create), not a question asked on every save.
  const updateCatalogContent = async () => {
    if (!catalogId || !questionnaireId) {
      return;
    }
    const patchRoute = `v2/${AnketaResourcesEnum.questionnaires}/${catalogId}/${questionnaireId}`;
    let saveError: unknown = null;
    const ok = await runSave(async () => {
      const plainObject = JSON.stringify(catalog.factsToObject());
      try {
        // The API client never throws on HTTP errors - it resolves with
        // success=false - so a failed PUT must be turned into a failure here or
        // the user is told a failed save succeeded.
        const resp = await Put<string>({
          path: patchRoute,
          responseType: "json",
          token: keycloak.token,
          value: { newContent: plainObject },
        });
        if (!resp.success) {
          throw new Error(
            resp.error?.message ?? `HTTP ${resp.status || "error"}`,
          );
        }
      } catch (err) {
        saveError = err;
        throw err;
      }
    });

    if (ok) {
      // The server now holds these answers; a lingering local draft would later
      // be merged over newer answers saved from another browser.
      anketaCtx.clearBrowserDraft();
      // Saving an incomplete questionnaire is normal (it is filled in over
      // time), so never block - but say what is still open and show it.
      const issues = countIssues(catalog, anketaCtx);
      evalCtx.setRevealIssues(true);
      if (issues.invalid > 0 || issues.missing > 0) {
        addToast({
          color: issues.invalid > 0 ? "warning" : "success",
          iconType: issues.invalid > 0 ? "alert" : "checkInCircleFilled",
          title: translate("anketa.questionnaire.action.update.success.header"),
          text:
            issues.invalid > 0
              ? translate("anketa.questionnaire.action.update.success.invalid", {
                  values: { missing: issues.missing, invalid: issues.invalid },
                  defaultMessage:
                    "Saved, but {{invalid}} answers are invalid and {{missing}} fields are still open. They are highlighted in the form.",
                })
              : translate("anketa.questionnaire.action.update.success.open", {
                  values: { missing: issues.missing },
                  defaultMessage:
                    "Saved. {{missing}} fields are still open.",
                }),
        });
      } else {
        addToast({
          color: "success",
          iconType: "checkInCircleFilled",
          title: translate("anketa.questionnaire.action.update.success.header"),
          text: translate("anketa.questionnaire.action.update.success.body"),
        });
      }
    } else {
      addToast({
        color: "danger",
        iconType: "alert",
        title: translate("anketa.questionnaire.action.update.error.header"),
        text: (
          <EuiCode>
            {typeof saveError === "string"
              ? saveError
              : saveError instanceof Error
                ? saveError.message
                : JSON.stringify(saveError)}
          </EuiCode>
        ),
      });
    }
  };

  const onModalClose = () => {
    setModal(null);
  };

  // Open the onboarding modal straight from a deep link so the operator can run
  // the bootstrap from the RocketChat "manual onboarding required" notification:
  // /questionnaires/:catalogId/:questionnaireId?onboard=1
  useEffect(() => {
    if (searchParams.get("onboard") !== "1" || !catalogId || !questionnaireId) {
      return;
    }
    setShowOnboard(true);
    // Consume the flag so the modal does not reopen on every re-render.
    searchParams.delete("onboard");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, catalogId, questionnaireId]);

  return (
    <EuiFlexGroup
      direction="row"
      alignItems="center"
      gutterSize="s"
      style={{
        margin: "5px",
      }}
    >
      {catalogId && questionnaireId && <QuestionnaireSaveIndicator />}

      {checkPermission(
        AnketaResourcesEnum.questionnaires,
        AnketaActionsEnum.update,
      ) &&
        catalogId &&
        questionnaireId && (
          <EuiFlexItem grow={false}>
            <EuiToolTip content={translate("anketa.questionnaires.update")}>
              <EuiButtonIcon
                aria-label={translate("anketa.questionnaires.update")}
                iconType="save"
                color="primary"
                size="s"
                display="fill"
                onClick={() => {
                  updateCatalogContent();
                }}
              />
            </EuiToolTip>
          </EuiFlexItem>
        )}

      {checkPermission(
        AnketaResourcesEnum.questionnaires,
        AnketaActionsEnum.invite,
      ) &&
        catalogId &&
        questionnaireId && (
          <EuiFlexItem grow={false}>
            <EuiToolTip content={translate("anketa.questionnaires.action.add")}>
              <EuiButtonIcon
                aria-label={translate("anketa.questionnaires.action.add")}
                iconType="users"
                color="success"
                size="s"
                display="fill"
                onClick={() => {
                  setModal(
                    <SelectOrInviteMemberModal onClose={onModalClose} />,
                  );
                }}
              />
            </EuiToolTip>
          </EuiFlexItem>
        )}
      {checkPermission(
        AnketaResourcesEnum.questionnaires,
        AnketaActionsEnum.create,
        { isInternal: true },
      ) && (
        <EuiFlexItem grow={false}>
          <EuiToolTip
            content={translate("anketa.create", {
              values: {
                type: translate("anketa.questionnaire"),
              },
            })}
          >
            <EuiButtonIcon
              aria-label={translate("anketa.create", {
                values: {
                  type: translate("anketa.questionnaire"),
                },
              })}
              iconType="indexEdit"
              color="success"
              size="s"
              display="fill"
              onClick={() => {
                setModalIsVisible(true);
              }}
            />
          </EuiToolTip>
        </EuiFlexItem>
      )}

      {checkPermission(
        AnketaResourcesEnum.questionnaires,
        AnketaActionsEnum.create,
        { isInternal: true },
      ) && (
        <EuiFlexItem grow={false}>
          <EuiPopover
            button={
              <EuiButtonIcon
                aria-label={translate(
                  "anketa.token.regenerate.questionnaire.button",
                  {
                    defaultMessage: "Manage API Token",
                  },
                )}
                iconType="tokenKey"
                color="success"
                size="s"
                display="fill"
                onClick={() => {
                  setShowApiToken(!showApiToken);
                }}
              />
            }
            isOpen={showApiToken}
            closePopover={() => setShowApiToken(false)}
            panelPaddingSize="s"
            anchorPosition="downLeft"
          >
            <AnketaApiToken
              catalog={catalogId}
              questionnaire={questionnaireId}
            />
          </EuiPopover>
        </EuiFlexItem>
      )}

      {canBootstrap && catalogId && questionnaireId && (
        <EuiFlexItem grow={false}>
          <EuiToolTip
            content={translate(
              canOnboardQuestionnaire
                ? "anketa.questionnaire.onboard.button"
                : "anketa.questionnaire.onboard.button.disabled",
              {
                defaultMessage: canOnboardQuestionnaire
                  ? "Create infrastructure project"
                  : "Create infrastructure project - needs a full VPC name (owner-project-stage)",
              },
            )}
          >
            <EuiButtonIcon
              aria-label={translate("anketa.questionnaire.onboard.button", {
                defaultMessage: "Create infrastructure project",
              })}
              iconType="package"
              color="warning"
              size="s"
              display="fill"
              isDisabled={!canOnboardQuestionnaire}
              onClick={() => {
                setShowOnboard(true);
              }}
            />
          </EuiToolTip>
        </EuiFlexItem>
      )}

      {modal}

      {canBootstrap && showOnboard && catalogId && questionnaireId && (
        <OnboardProjectModal
          catalogId={catalogId}
          questionnaireId={questionnaireId}
          vpcName={qName}
          onClose={() => setShowOnboard(false)}
        />
      )}
    </EuiFlexGroup>
  );
};
